import React, { useState, useEffect, useRef } from 'react';
import { useSidebar, SidebarTrigger } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { userService } from '@/_services';
import * as signalR from '@aspnet/signalr';
import {
    Bell,
    Check,
} from 'lucide-react';
// Import domain-specific notification icons
import GlicIcon from '@/components/css/dark/img/gh_icon_new.png';
import RxIcon from '@/components/css/dark/img/nls-icon.png';

// Helper to check if it's RxHacker theme
const isRxHackerTheme = () => {
    const host = window.location.host.toLowerCase();
    const domain = window.location.host.split('.').slice(-2).join('.').toLowerCase();
    return domain === "rxhacker.com" || 
           domain === "localhost:5464" || 
           host === "tempfilter.23byt.es" || 
           host === "reporting.nulifespanrx.com";
};

// Get logo URL based on domain
const getLogoUrl = () => {
    const host = window.location.host.toLowerCase();
    const domain = window.location.host.split('.').slice(-2).join('.').toLowerCase();
    
    if (host === "reporting.patientrxdiscount.com") {
        return '/Patient-Rx-Discount.svg';
    } else if (domain === "glichealth.com" && host !== "tempfilter.23byt.es") {
        return '/GlicHealth_logo.png';
    } else if (domain === "rxhacker.com" || domain === "localhost:5464" || 
               host === "tempfilter.23byt.es" || host === "reporting.nulifespanrx.com") {
        return '/Rxhacker_logo_new.png';
    } else if (domain === "honestdiscounts.com") {
        return '/hdrx-logo.svg';
    }
    return '/GlicHealth_logo.png';
};

// Get user initials
const getUserInitials = (username) => {
    if (!username) return '--';
    
    const spaceSplit = username.split(' ');
    const hyphenSplit = username.split('-');
    
    if (spaceSplit.length > 1) {
        return (spaceSplit[0].charAt(0) + spaceSplit[1].charAt(0)).toUpperCase();
    } else if (hyphenSplit.length > 1) {
        return (hyphenSplit[0].charAt(0) + hyphenSplit[1].charAt(0)).toUpperCase();
    }
    // For single word names, return first 2 letters
    return username.slice(0, 2).toUpperCase();
};

export function AppNavbar() {
    const sidebar = useSidebar();
    const [userData, setUserData] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const connectionRef = useRef(null);

    const userRole = userData?.isBrokerTypeUser ? 'Broker' : userData?.userrole;
    const isRxTheme = isRxHackerTheme();
    const logoUrl = getLogoUrl();

    // Fetch user data from localStorage (remember me) or sessionStorage (current session)
    useEffect(() => {
        try {
            const userStorage = localStorage.getItem('user') || sessionStorage.getItem('user');
            const user = userStorage ? JSON.parse(userStorage) : null;
            setUserData(user);
        } catch (error) {

        }
    }, []);

    // Fetch notifications and setup SignalR when userData is available
    useEffect(() => {
        if (userData) {
            fetchNotifications();
            setupSignalR();
        }

        return () => {
            // Cleanup SignalR connection
            if (connectionRef.current) {
                connectionRef.current.stop();
            }
        };
    }, [userData]);

    const fetchNotifications = async () => {
        try {
            const roleEmail = userData.email;
            let roleType = userData.userrole;
            const roleId = userData.id;
            
            if (userData.isBrokerTypeUser) roleType = "Broker";
            
            const data = await userService.GetUserNotification(roleEmail, roleType, roleId);
            if (data && data.length > 0) {
                setNotifications(data);
                const unread = data.filter(item => item.isAcknowledge === 0);
                setUnreadCount(unread.length);
            }
        } catch (error) {

        }
    };

    const setupSignalR = () => {
        try {
            const connection = new signalR.HubConnectionBuilder()
                .withUrl("/notification", {
                    skipNegotiation: true,
                    transport: signalR.HttpTransportType.WebSockets
                })
                .build();

            connection.start().then(() => {
                connection.on("ReceiveMessage", (whemail, status, noteUserType, minset) => {
                    if (noteUserType.toLowerCase() !== 'unknowntypeuser') {
                        fetchNotifications();
                    }
                });
            }).catch(err => {

            });

            connectionRef.current = connection;
        } catch (err) {

        }
    };

    const handleBellClick = async () => {
        setIsNotificationOpen(true);
        
        if (unreadCount > 0 && userData) {
            try {
                let roleType = userData.userrole;
                if (userData.isBrokerTypeUser) roleType = "Broker";
                
                await userService.NotificationAcknowledge(userData.id, roleType, userData.email);
                setUnreadCount(0);
            } catch (error) {

            }
        }
    };

    const formatNotificationTime = (timeStr) => {
        return timeStr || '';
    };

    return (
        <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-card border-b border-border">
            <div className="flex items-center justify-between h-full px-4">
                {/* Left section - Sidebar toggle + Logo */}
                <div className="flex items-center gap-4">
                    {/* Sidebar toggle */}
                    <SidebarTrigger className="-ml-1" />

                    {/* Logo */}
                    <a href="/" className="flex items-center">
                        <img 
                            src={window.location.origin + logoUrl} 
                            alt="Logo" 
                            className="w-[110px] sm:w-[170px] h-auto object-contain"
                            crossOrigin="anonymous"
                        />
                    </a>
                </div>

                {/* Right section - Notifications + User */}
                <div className="flex items-center gap-2">
                    {/* Notifications */}
                    <Popover open={isNotificationOpen} onOpenChange={setIsNotificationOpen}>
                        <PopoverTrigger asChild>
                            <Button 
                                variant="ghost" 
                                size="icon"
                                className="relative cursor-pointer"
                                onClick={handleBellClick}
                            >
                                <Bell className="h-5 w-5" />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center">
                                        {unreadCount > 9 ? '9+' : unreadCount}
                                    </span>
                                )}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent 
                            className="w-80 p-0" 
                            align="end"
                            sideOffset={8}
                        >
                            <div className="p-4 border-b border-border">
                                <h3 className="font-semibold">Notifications</h3>
                            </div>
                            <ScrollArea className="h-[300px]">
                                {notifications.length > 0 ? (
                                    <div className="divide-y divide-border">
                                        {notifications.map((notification, index) => (
                                            <div 
                                                key={index}
                                                className={cn(
                                                    "p-4 hover:bg-accent/50 transition-colors",
                                                    notification.isAcknowledge === 0 && "bg-primary/5"
                                                )}
                                            >
                                                <div className="flex gap-3">
                                                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                                        <img 
                                                            src={isRxTheme ? RxIcon : GlicIcon} 
                                                            alt="notification icon" 
                                                            className="h-8 w-8 object-contain"
                                                        />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm text-foreground">
                                                            {notification.not_Content}
                                                        </p>
                                                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                                            <span>{formatNotificationTime(notification.not_Time)}</span>
                                                            {notification.not_Owner && (
                                                                <>
                                                                    <span>•</span>
                                                                    <span>{notification.not_Owner}</span>
                                                                </>
                                                            )}
                                                            {notification.isClosed && (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs">
                                                                    <Check className="h-3 w-3" />
                                                                    Resolved
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-full py-8 text-center">
                                        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
                                            <Bell className="h-6 w-6 text-muted-foreground" />
                                        </div>
                                        <p className="text-sm font-medium">No Notifications</p>
                                        <p className="text-xs text-muted-foreground">You are up to date</p>
                                    </div>
                                )}
                            </ScrollArea>
                        </PopoverContent>
                    </Popover>

                    <Separator orientation="vertical" className="h-8 mx-2 hidden sm:block" />

                    {/* User profile - Avatar with name and role */}
                    <div className="flex items-center gap-3 px-2">
                        <Avatar className="h-9 w-9">
                            <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
                                {getUserInitials(userData?.username)}
                            </AvatarFallback>
                        </Avatar>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className="text-left max-w-[160px] min-w-0 cursor-default">
                                        <p className="text-sm font-medium leading-none text-foreground truncate">{userData?.username}</p>
                                        <p className="text-xs text-muted-foreground mt-0.5">{userRole}</p>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-[200px] w-full break-words">
                                    <p>{userData?.username}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>
            </div>
        </header>
    );
}

export default AppNavbar;

